/* eslint-disable max-lines */
// Hooks, libs and types
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {getExternalConnectionType, mapObject} from "../../utils";
import {analyticsSendEvent} from "../../libs/analytics";
import {
  DetailedSessionFeedbackInput,
  EducationSessionResult,
  Challenge,
  ChallengeMode,
  ParametricResultDescription,
  ResultDescription,
  EducationSessionErrorReason
} from "../../schema";
import {gql} from "@apollo/client";

// Redux
import {useDispatch, useSelector} from "react-redux";
import isEqual from "lodash/isEqual";
import {start, submitFeedback} from "../../redux/player/actions";
import {PlayerReduxState} from "../../redux/player/types";
import {canRestart, canDisplayResults} from "../../redux/player/selectors";

// Components
import Button from "../../ui/button";
import FeedbackForm from "../results/FeedbackForm";
import ResultsDisplay from "../results/ResultsDisplay";

import classes from "./Results.module.css";
import {getProcessedResultDescriptions} from "../../utils/player/utils";

const RESULT_ANALYSIS_TIMEOUT_MS = 2 * 60 * 1000;

export type QueryData = {
  results: Pick<
    EducationSessionResult,
    | "passed"
    | "score"
    | "time"
    | "usedHints"
    | "record"
    | "recordState"
    | "requestFeedback"
    | "generalSentimentNeutral"
    | "generalSentimentPositive"
    | "generalSentimentNegative"
    | "competenceAnalysisState"
    | "competenceResults"
    | "parametricResults"
    | "finishedScenariosIds"
    | "isUserInputAnalysisReady"
    | "resultFeedback"
  >;
  nextChallenge: Pick<Challenge, "id" | "accessToken"> | null;
};

type props = Omit<React.ComponentProps<"div">, "results"> & {
  challenge: Pick<
    Challenge,
    | "id"
    | "mode"
    | "languageCode"
    | "showDetailedResults"
    | "resultMessage"
    | "showResultsForEachParameter"
    | "displayParametricResultMode"
    | "isSessionResultFeedbackEnabled"
 >;
  results: QueryData["results"];
  nextChallenge?: QueryData["nextChallenge"];
  projectId?: string;
  descriptions?: ResultDescription[];
  parametricDescriptions?: ParametricResultDescription[];
  onFinish: () => void;
 sessionId?: string;
};

const Results = function ({challenge, results, nextChallenge, descriptions,
   parametricDescriptions, projectId, onFinish, sessionId, ...props}: props) {
  const {t} = useTranslation();

  const dispatch = useDispatch();
  const {canRestart, canDisplayResults, isInterrupted, interruptReason} = useSelector(
    Results.selector,
    isEqual,
  );

  const onFeedback = useCallback(
    (score?: number, data?: DetailedSessionFeedbackInput, message?: string, contacts?: string) => {
      analyticsSendEvent("leaveFeedback", {
        from: "player",
        score,
        message,
        contacts,
        ...mapObject(data ?? {}, (item) => !!item),
      });

      dispatch(
        submitFeedback({
          data: data ?? null,
          score: score ?? null,
          message: message ?? null,
          contacts: contacts ?? null,
        }),
      );
    },
    [dispatch],
  );
  const onRestart = useCallback(() => dispatch(start()), [dispatch]);

  const canFinish = !!getExternalConnectionType()
  const isTestWithCustomScores = challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES;
  const isMultiParamDescriptions = challenge.showResultsForEachParameter;

  const [isAnalysisTakingTooLong, setIsAnalysisTakingTooLong] = useState(false);
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    const elapsed = Date.now() - mountTimeRef.current;
    if (elapsed >= RESULT_ANALYSIS_TIMEOUT_MS) {
      setIsAnalysisTakingTooLong(true);
      return;
    }
    const timer = setTimeout(() => setIsAnalysisTakingTooLong(true), RESULT_ANALYSIS_TIMEOUT_MS - elapsed);
    return () => clearTimeout(timer);
  }, []);

  const proccesedResultDescription = useMemo(
    () => getProcessedResultDescriptions(results, descriptions ?? [], parametricDescriptions ?? [], challenge),
    [results, descriptions, parametricDescriptions, challenge],
  );

  const matchedDescription = typeof proccesedResultDescription === "string" ? proccesedResultDescription : undefined;
  const descriptionsByParams = Array.isArray(proccesedResultDescription) ? proccesedResultDescription : [];

  // const nextChallengeHref = useMemo(() => {
  //   if (!nextChallenge) {
  //     return undefined;
  //   }
  //   const url = new URL(reverse("playerPlay", {
  //     id: isShared ? getSharedId(nextChallenge) : nextChallenge.id
  //   }), document.baseURI);
  //   if (isShared && projectId) {
  //     url.searchParams.set("projectId", projectId);
  //   }
  //   // Making relative URL because react-router don't accept absolute
  //   return url.pathname + url.search;
  // }, [nextChallenge, isShared, projectId])

  if (isInterrupted) {
    let interruptText = t("components.Results.interrupt.default");

    switch (interruptReason) {
      case EducationSessionErrorReason.BAD_WORD:
        interruptText = t("components.Results.interrupt.badWords");
        break;
      case EducationSessionErrorReason.CHALLENGE_TIME_LIMIT_EXPIRED:
        interruptText = t("components.Results.interrupt.timeLimitExpired");
        break;
      default:
        console.error(`No special text for interrupt reason: ${interruptReason}`)
        break;
    }

    return (
      <div className={classes.root} {...props}>
        <h2 className={classes.title}>{t("components.Results.title")}</h2>
        <span className={classes.interruptText}>{interruptText}</span>
        {canRestart ? (
          <Button size="l" color="primary" onClick={onRestart}>
            {t("components.Results.restart")}
          </Button>
        ) : (
          <Button size="l" color="primary" onClick={onFinish}>
            {t("components.Results.finish")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <ResultsDisplay
      adaptive
      className={challenge.showDetailedResults ? classes.resultsDisplay : classes.resultsDisplayCenter}
      challenge={challenge}
      results={results}
      canDisplayResults={canDisplayResults}
      resultMessage={challenge.resultMessage ?? undefined}
      showDetailed={challenge.showDetailedResults ?? true}
      customScores={isTestWithCustomScores}
      matchedDescription={matchedDescription ?? undefined}
      multiDescriptions={isMultiParamDescriptions ? descriptionsByParams : undefined}
      sessionResultFeedback={results.resultFeedback ?? undefined}
      isAnalysisTakingTooLong={isAnalysisTakingTooLong}
      {...props}
    >
      <div className={challenge.showDetailedResults ? classes.buttonContainer : classes.buttonContainerCenter}>
        {canRestart ? (
          <Button color="primary" className={classes.btn} onClick={onRestart}>
            {t("components.Results.restart")}
          </Button>
        ) : canFinish ? (
          <Button color="primary" className={classes.btn} onClick={onFinish}>
            {t("components.Results.finish")}
          </Button>
        ) : (
          <span>{t("components.Results.returnToLms")}</span>
        )}
      </div>

      <FeedbackForm adaptive
        modalVisibleByDefault
        className={classes.feedback}
        onFeedback={onFeedback}
      />
    </ResultsDisplay>
  );
};

Results.selector = (state: PlayerReduxState) => ({
  canRestart: canRestart(state),
  canDisplayResults: canDisplayResults(state),
  isInterrupted: state.interrupted,
  interruptReason: state.interruptReason,
});

Results.fragments = {
  root: gql`
    fragment Results on EducationSessionResult {
      passed
      score
      time
      usedHints

      record
      recordState

      generalSentimentNeutral
      generalSentimentPositive
      generalSentimentNegative

      generalEmotionNeutral
      generalEmotionJoy
      generalEmotionEnthusiasm
      generalEmotionAnger
      generalEmotionGuilt
      generalEmotionShame
      generalEmotionFear
      generalEmotionDisgust
      generalEmotionSadness
      generalEmotionSurprise

      requestFeedback
      competenceAnalysisState
      finishedScenariosIds

      resultFeedback {
        text
        feedbackStatus
      }
      parametricResults {
        parameterName
        rawSum
        percentage
      }
      competenceResults {
        competence
        score
        feedback
        indicators {
          indicator
          isPresent
          example
        }
      }

      isUserInputAnalysisReady
    }
  `,
  nextChallenge: gql`
    fragment NextChallenge on Challenge {
      id
      accessToken
    }
  `,
};

export default Results;
